import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Printer, Download } from 'lucide-react';
import { useDivisionContext } from '../context/DivisionContext';
import { useToast } from '../context/ToastContext';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatDay = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

// Shared column widths so every per-month table aligns identically.
const calendarColGroup = (
  <colgroup>
    <col style={{ width: '14%' }} />
    <col style={{ width: '8%' }} />
    <col style={{ width: '12%' }} />
    <col />
    <col style={{ width: '19%' }} />
    <col style={{ width: '8%' }} />
  </colgroup>
);

const ClubCalendar = () => {
  const { clubId } = useParams();
  const { seasons, selectedSeasonId, setSelectedSeasonId, loading: contextLoading } = useDivisionContext();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hostedOnly, setHostedOnly] = useState(false);

  const fetchCalendar = useCallback(async () => {
    if (!selectedSeasonId || !clubId) return;
    try {
      const res = await axios.get(`/api/clubs/${clubId}/fixture-calendar`, {
        params: { seasonId: selectedSeasonId, scope: hostedOnly ? 'hosted' : undefined },
      });
      setData(res.data);
    } catch (e) {
      console.error(e);
      setData(null);
      toast.error(e?.response?.data?.error || 'Failed to load club calendar');
    } finally {
      setLoading(false);
    }
  }, [clubId, selectedSeasonId, hostedOnly, toast]);

  useEffect(() => {
    if (contextLoading) return;
    fetchCalendar();
  }, [contextLoading, fetchCalendar]);

  const rows = useMemo(() => {
    const list = (data?.fixtures || []).filter((f) => f.match_date);
    return list.map((f) => {
      const d = new Date(f.match_date);
      return {
        ...f,
        monthKey: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      };
    });
  }, [data]);

  const months = useMemo(() => {
    const byMonth = new Map();
    for (const f of rows) {
      if (!byMonth.has(f.monthKey)) byMonth.set(f.monthKey, []);
      byMonth.get(f.monthKey).push(f);
    }
    return Array.from(byMonth.entries());
  }, [rows]);

  const unscheduled = useMemo(
    () => (data?.fixtures || []).filter((f) => !f.match_date),
    [data]
  );

  const seasonName = seasons.find((s) => s.id === selectedSeasonId)?.name || '';

  const downloadCsv = () => {
    const header = ['Date', 'Day', 'Division', 'Home Team', 'Away Team', 'Venue', 'Type'];
    const lines = [header.join(',')];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    for (const f of rows) {
      const d = new Date(f.match_date);
      lines.push([
        formatDay(f.match_date),
        DAY_NAMES[d.getDay()],
        f.division_name || '',
        f.home_team_name,
        f.away_team_name,
        f.home_club_name || '',
        f.match_type === 'cup' ? 'Cup' : 'League',
      ].map(esc).join(','));
    }
    for (const f of unscheduled) {
      lines.push([
        'TBC', '',
        f.division_name || '',
        f.home_team_name,
        f.away_team_name,
        f.home_club_name || '',
        f.match_type === 'cup' ? 'Cup' : 'League',
      ].map(esc).join(','));
    }
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(data?.club?.name || 'club').replace(/[^a-z0-9]+/gi, '-')}-fixtures-${seasonName.replace(/[^a-z0-9]+/gi, '-')}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-center py-8">Loading club calendar...</div>;
  if (!data?.club) return <div className="text-center py-8">Club not found</div>;

  const { club } = data;

  return (
    <div className="space-y-6 print-calendar">
      <div className="no-print flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{club.name} — Fixture Calendar</h1>
          {club.address && <div className="text-sm text-gray-500 mt-1">{club.address}</div>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-600">Season</span>
          <select
            className="input"
            value={selectedSeasonId}
            onChange={(e) => setSelectedSeasonId(e.target.value)}
          >
            {seasons.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}{s.status === 'active' ? ' (active)' : ''}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-sm text-gray-600 whitespace-nowrap">
            <input
              type="checkbox"
              className="accent-blue-600"
              checked={hostedOnly}
              onChange={(e) => setHostedOnly(e.target.checked)}
            />
            Venue only
          </label>
          <button type="button" className="btn btn-secondary flex items-center gap-2" onClick={downloadCsv}>
            <Download size={16} />
            CSV
          </button>
          <button type="button" className="btn btn-primary flex items-center gap-2" onClick={() => window.print()}>
            <Printer size={16} />
            Print / PDF
          </button>
        </div>
      </div>

      <div className="print-sheet bg-white rounded-lg border border-gray-200 p-6">
        <div className="mb-4 items-center gap-4 print-header">
          <img src="/logo.png" alt="Bray & District Table Tennis League" className="h-16 w-auto print-logo" />
          <div>
            <div className="text-2xl font-bold text-gray-900 print-league-title">Bray &amp; District Table Tennis League</div>
            <div className="text-lg font-semibold text-gray-800 mt-0.5">{club.name}{club.address ? ` — ${club.address}` : ''}</div>
            <div className="text-sm text-gray-600">
              {hostedOnly ? 'Venue fixture calendar' : 'Club fixture calendar'} · Season {seasonName} · {rows.length + unscheduled.length} fixture{rows.length + unscheduled.length === 1 ? '' : 's'}
            </div>
          </div>
        </div>

        {months.map(([monthKey, fixtures]) => {
          const [y, m] = monthKey.split('-').map(Number);
          const monthName = new Date(y, m - 1, 1).toLocaleString(undefined, { month: 'long', year: 'numeric' });
          return (
            <div key={monthKey} className="mb-6 print-month">
              <div className="text-sm font-bold uppercase tracking-wide text-gray-700 border-b-2 border-gray-800 pb-1 mb-2">
                {monthName}
              </div>
              <table className="w-full text-sm table-fixed">
                {calendarColGroup}
                <thead>
                  <tr className="text-left text-gray-600 border-b border-gray-300">
                    <th className="py-1 pr-3 font-medium">Date</th>
                    <th className="py-1 pr-3 font-medium">Day</th>
                    <th className="py-1 pr-3 font-medium">Division</th>
                    <th className="py-1 pr-3 font-medium">Fixture</th>
                    <th className="py-1 pr-3 font-medium">Venue</th>
                    <th className="py-1 font-medium">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {fixtures.map((f) => {
                    const d = new Date(f.match_date);
                    const clubAtHome = f.home_club_id === club.id;
                    return (
                      <tr key={f.id} className="border-b border-gray-200 last:border-0">
                        <td className="py-1.5 pr-3 whitespace-nowrap">{formatDay(f.match_date)}</td>
                        <td className="py-1.5 pr-3">{DAY_NAMES[d.getDay()]}</td>
                        <td className="py-1.5 pr-3 whitespace-nowrap">{f.division_name || '—'}</td>
                        <td className="py-1.5 pr-3">
                          <span className={clubAtHome ? 'font-medium' : 'text-gray-600'}>{f.home_team_name}</span>
                          <span className="text-gray-400"> vs </span>
                          <span className={clubAtHome ? 'text-gray-600' : 'font-medium'}>{f.away_team_name}</span>
                        </td>
                        <td className="py-1.5 pr-3 whitespace-nowrap">
                          {clubAtHome ? f.home_club_name || 'Home' : `at ${f.home_club_name || f.home_team_name}`}
                        </td>
                        <td className="py-1.5">{f.match_type === 'cup' ? 'Cup' : 'League'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}

        {unscheduled.length > 0 && (
          <div className="mb-6 print-month">
            <div className="text-sm font-bold uppercase tracking-wide text-gray-700 border-b-2 border-gray-800 pb-1 mb-2">
              To be scheduled
            </div>
            <table className="w-full text-sm table-fixed">
              {calendarColGroup}
              <tbody>
                {unscheduled.map((f) => (
                  <tr key={f.id} className="border-b border-gray-200 last:border-0">
                    <td colSpan={2} className="py-1.5 pr-3 whitespace-nowrap text-gray-500">TBC</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{f.division_name || '—'}</td>
                    <td className="py-1.5 pr-3">
                      <span className={f.home_club_id === club.id ? 'font-medium' : 'text-gray-600'}>{f.home_team_name}</span>
                      <span className="text-gray-400"> vs </span>
                      <span className={f.home_club_id === club.id ? 'text-gray-600' : 'font-medium'}>{f.away_team_name}</span>
                    </td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">
                      {f.home_club_id === club.id ? f.home_club_name || 'Home' : `at ${f.home_club_name || f.home_team_name}`}
                    </td>
                    <td className="py-1.5">{f.match_type === 'cup' ? 'Cup' : 'League'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {months.length === 0 && unscheduled.length === 0 && (
          <div className="text-gray-500 text-center py-8">No fixtures scheduled at this club for this season.</div>
        )}

        <div className="text-xs text-gray-400 border-t border-gray-200 pt-2 mt-4">
          Bray &amp; District Table Tennis League · {window.location.host} · generated {new Date().toLocaleDateString()}
        </div>
      </div>
    </div>
  );
};

export default ClubCalendar;
